import { memo, useEffect, useMemo, type PropsWithChildren } from 'react';

import { Loader } from '@/js/libs/ui';
import { useAuth, useGetIdentity } from '@/js/libs/use-auth';

import { useCompanySwitchContext } from 'src/modules/CompaniesSwitch/context.tsx';
import { SELECTED_COMPANY_HEADER_NAME } from './constants.ts';
import { AuthContext } from './context.ts';
import { useAxiosIntegration, useRevertIgnoredCompanySwitch } from './hooks/index.ts';

export const AuthContextProvider = memo(function AuthContextProvider({ children }: PropsWithChildren) {
  const { token } = useAuth();
  const company = useCompanySwitchContext('company');
  const revertIgnoredCompanySwitch = useRevertIgnoredCompanySwitch();
  const { getIdentity, loading, initialized } = useGetIdentity();

  const headers = useMemo(() => {
    const requestHeaders: Record<string, string> = {
      'Cache-Control': 'no-cache',
      'If-Modified-Since': 'Sun, 06 Oct 2013 01:16:45 GMT',
    };
    if (token) {
      requestHeaders.Authorization = `Bearer ${token}`;
    }
    if (company) {
      requestHeaders[SELECTED_COMPANY_HEADER_NAME] = window.btoa(
        JSON.stringify({
          id: company.id,
          companyUUID: company.companyUUID,
        }),
      );
    }

    return requestHeaders;
  }, [company, token]);

  const value = useMemo(() => ({ headers }), [headers]);

  // Registers the interceptors before the identity effect below: React runs a component's effects
  // in declaration order, so the identity request on mount already goes through them.
  useAxiosIntegration({ headers });

  useEffect(() => {
    // Reloads the identity on mount and after every company switch, then checks it against the company it was
    // requested for: the closure holds that company, and an aborted load never reaches the check.
    getIdentity({ selectedCompanyId: company?.id }).then(
      (user) => revertIgnoredCompanySwitch(company, user),
      () => {
        // a failed load is handled by useGetIdentity's onFail; an aborted one is replaced by the newer load
      },
    );
  }, [company, getIdentity, revertIgnoredCompanySwitch]);

  if (loading || !initialized) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
});
